import React, { use, useState } from 'react';
import AdminHeader from '../layout/AdminHeader';
import DashboardHeader from '../../dashboard/layout/DashboardHeader';
import VideoGalleryWithFilters from '../gallery/VideoGalleryWithFilters';
import HeaderNotification from '../../notifications/HeaderNotification';
import { useLogout } from '@/hooks/auth/useLogout';
import { useWebSocketSync } from '@/hooks/querry_hooks/common/useWebSocketSync';
import useAdminHeaderNotifications from '@/hooks/admin/useAdminHeaderNotifications';
import { PicassoTitle } from '../../ui/BrandingComponents';
import { DeviceContext } from '@/hooks/common/DeviceContext';

interface PicassoContentReviewProps {
  isAdmin: boolean;
  userEmail: string | null;
}

/**
 * PicassoContentReview - Content review and management page for administrators
 * 
 * Provides a comprehensive interface for administrators to review, approve, reject,
 * and manage user-generated video content. Features real-time notifications for
 * new content and advanced filtering capabilities. Image review functionality
 * is planned for future implementation.
 * 
 * Features:
 * - Tabbed interface for Videos and Images (Images coming soon)
 * - Real-time content notifications
 * - Advanced video filtering and sorting
 * - Content approval/rejection workflows
 * - Video pinning and management
 * - Responsive design with mobile support
 * - Error handling and loading states
 * - WebSocket integration for live updates
 * 
 * Key Components:
 * - HeaderNotification for real-time alerts
 * - AdminHeader for navigation and user controls
 * - VideoGalleryWithFilters for video content management
 * - PicassoTitle for branded page header
 */
const PicassoContentReview: React.FC<PicassoContentReviewProps> = ({ isAdmin, userEmail }) => {

  // responsive
  const { isMobile } = use(DeviceContext) ?? { isMobile: false, isTablet: false };
  
  // Logout functionality
  const { logout } = useLogout();

  // WebSocket sync for real-time React Query cache updates
  useWebSocketSync(userEmail, isAdmin);

  // Admin header notifications
  const { 
    showNotification, 
    notificationMessage, 
    handleReload: handleNotificationReload, 
    dismissNotification 
  } = useAdminHeaderNotifications(userEmail || '', !!userEmail);

  // Page value state for switching between videos and images (following dashboard pattern)
  const [pageValue, setPageValue] = useState<'Videos' | 'Images' | 'ExtensionRequests'>('Videos');

  /**
   * Handles page refresh when title is clicked
   * Provides a quick way to reload content and clear any cached state
   */
  const handleRefresh = () => {
    globalThis.location.reload();
  };

  return (
    <div className="w-full bg-transparent flex flex-col min-h-screen">
      {/* Real-time notification banner for new videos */}
      <HeaderNotification
        show={showNotification}
        message={notificationMessage}
        onReload={handleNotificationReload}
        onDismiss={dismissNotification}
      />
      
      {/* Navigation header - Admin header for admins, Dashboard header for content creators */}
      {isAdmin ? (
        <AdminHeader
          currentAdmin={userEmail || ''}
          onLogout={logout}
        />
      ) : (
        <DashboardHeader isAdmin={isAdmin} userEmail={userEmail} />
      )}

      {/* Main content area */}
      <main className={`flex-1 w-full px-4 sm:px-6 ${isMobile ? 'py-0' : 'py-8'}  sm:py-12 bg-white`}>

        {/* Page title with Picasso branding */}
        <div className="text-center mb-8 flex justify-center">
          <div className="relative">
            {!isMobile && 
              <PicassoTitle 
                text="Review"
                size="lg"
                gradient="picassoTitle"
                onClick={handleRefresh}
                style={{ 
                  marginBottom: '1rem', 
                  maxWidth: 'fit-content',
                  cursor: 'pointer'
                }}
              />
            }
          </div>
        </div>

        {/* Content management interface */}
        <div className="mx-auto">
          <VideoGalleryWithFilters 
            isAdmin={isAdmin} 
            userEmail={userEmail} 
            pageValue={pageValue}
            setPageValue={setPageValue}
          />
        </div>
      </main>
    </div>
  );
};

export default PicassoContentReview;
